import { useBenchmark, type BenchmarkParams, type BenchmarkResult, type FlashListRef } from '@shopify/flash-list';
import { useEffect, useState, type RefObject } from 'react';
import { Alert } from 'react-native';

const DEFAULT_BENCHMARK_ITEM_COUNT = 200;

interface UseFlashListBenchmarkArgs<ItemT> {
  enabled?: boolean;
  items: ItemT[];
  innerRef: RefObject<FlashListRef<ItemT>>;
  benchmarkParams?: BenchmarkParams;
  onBenchmarkComplete?: (result: BenchmarkResult) => void;
  itemCount?: number;
  title?: string;
  isPending?: boolean;
  isRefetching?: boolean;
}

export function useFlashListBenchmark<ItemT>({
  enabled = false,
  items,
  innerRef,
  benchmarkParams,
  onBenchmarkComplete,
  itemCount = DEFAULT_BENCHMARK_ITEM_COUNT,
  title = 'FlashList benchmark',
  isPending = false,
  isRefetching = false,
}: UseFlashListBenchmarkArgs<ItemT>) {
  const benchmarkItems = (() => {
    if (!enabled) return items;
    const count = Math.max(items.length, itemCount);
    const len = items.length;
    if (len === 0) return [];
    const arr: ItemT[] = new Array(count);
    const isObject = typeof items[0] === 'object';
    for (let i = 0; i < count; i++) {
      arr[i] = (isObject ? { ...(items[i % len] as object) } : items[i % len]) as ItemT;
    }
    return arr;
  })();

  const [isUsingBenchmarkData, setIsUsingBenchmarkData] = useState(false);
  const renderedItems = isUsingBenchmarkData ? benchmarkItems : items;

  const handleBenchmarkComplete = (result: BenchmarkResult) => {
    setIsUsingBenchmarkData(false);
    Alert.alert(title, result.formattedString ?? JSON.stringify(result, null, 2));
    onBenchmarkComplete?.(result);
  };

  const { startBenchmark, isBenchmarkRunning } = useBenchmark(innerRef, handleBenchmarkComplete, {
    ...benchmarkParams,
    startManually: true,
  });

  const isBenchmarkReady = enabled && !isPending && !isRefetching && items.length > 0;

  const onStartBenchmark = () => {
    if (!isBenchmarkReady || isBenchmarkRunning) return;
    setIsUsingBenchmarkData(true);
  };

  useEffect(() => {
    if (!isUsingBenchmarkData || isBenchmarkRunning || !isBenchmarkReady) return;

    const list = innerRef.current as FlashListRef<ItemT> | null;
    if (list !== null) {
      list.scrollToOffset({ animated: false, offset: 0 });
    }
    startBenchmark();
  }, [innerRef, isBenchmarkReady, isBenchmarkRunning, isUsingBenchmarkData, startBenchmark]);

  return {
    renderedItems,
    isBenchmarkReady,
    isBenchmarkRunning,
    isUsingBenchmarkData,
    onStartBenchmark,
  };
}
